import { resetEcosystem } from '@crow/ui';
import { type CloudLinkStatus, cloudLinkStatusSchema } from '@crowdex/agent-contracts/api';
import { apiFetch } from '@crowdex/host-kit/renderer/http';

/*
 * The link of this session's server to Crowdex Cloud. The server keeps the
 * cloud session; the page reaches the cloud's API through `/cloud/api/*`, and
 * the requests about a conversation living on the cloud are relayed there.
 */

const JSON_HEADERS = { 'content-type': 'application/json' } as const;
const LINK_CHANGED = 'crowdex:cloud-link-changed';

/**
 * Tells the session the link changed, so the cloud's conversations join or leave the sidebar, and
 * the product switcher reads the Crowlingo apps again (on a computer, they are the linked cloud's).
 */
export function notifyCloudLinkChanged(): void {
  resetEcosystem();
  window.dispatchEvent(new Event(LINK_CHANGED));
}

export function onCloudLinkChanged(listener: () => void): () => void {
  window.addEventListener(LINK_CHANGED, listener);
  return () => window.removeEventListener(LINK_CHANGED, listener);
}

/** A path on the cloud when `cloud` is set, on the session's server otherwise. */
export function cloudApiPath(cloud: boolean | undefined, path: string): string {
  return cloud === true ? `/cloud${path}` : path;
}

export async function loadCloudLink(): Promise<CloudLinkStatus> {
  return cloudLinkStatusSchema.parse(await apiFetch('/api/cloud-link', { cache: 'no-store' }));
}

/** Opens the cloud's sign-in in the browser; the link is saved when the user approves it. */
export async function startCloudLink(): Promise<void> {
  const { url } = await apiFetch<{ url: string }>('/api/cloud-link', { method: 'POST' });
  window.open(url, '_blank', 'noopener,noreferrer');
}

export async function unlinkCloud(): Promise<void> {
  await apiFetch('/api/cloud-link', { method: 'DELETE' });
}

/** Starts a new conversation on the cloud (or back on the session's server). */
export async function placeNewConversation(conversationId: string, cloud: boolean): Promise<void> {
  await apiFetch('/api/cloud-link/conversations', {
    method: 'PUT',
    headers: JSON_HEADERS,
    body: JSON.stringify({ conversationId, cloud }),
  });
}

/** On the cloud: approves the link the Crowdex app of this computer asked for. */
export async function authorizeCloudLink(input: {
  challenge: string;
  callback: string;
  state: string;
}): Promise<string> {
  const { redirect } = await apiFetch<{ redirect: string }>('/api/cloud-link/authorize', {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(input),
  });
  return redirect;
}
